import { SearchIcon } from "lucide-react";

export type ProviderAvailbilityAnimationProps = {
  /** Label for the current 4-week search window (props-driven). */
  rangeLabel: string;
};

export function ProviderAvailabilityAnimation({
  rangeLabel
}: ProviderAvailbilityAnimationProps) {
  // Simple, stable calendar grid (dummy): 5 rows x 7 columns
  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];
  const dayCells = Array.from({ length: 35 }, (_, i) => i + 1);

  return (
    <div aria-hidden className="w-full max-w-[520px]">
      <style>
        {`
          @keyframes poc-float {
            0% { transform: translateY(0px) rotate(-1deg); }
            50% { transform: translateY(6px) rotate(-1deg); }
            100% { transform: translateY(0px) rotate(-1deg); }
          }
          @keyframes poc-scan-y {
            0% { transform: translateY(-35%); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translateY(270%); opacity: 0; }
          }
          @keyframes poc-lens-travel {
            0%   { left: 10%; top: 14%; }
            8%   { left: 42%; top: 10%; }
            16%  { left: 58%; top: 22%; }
            24%  { left: 28%; top: 28%; }
            32%  { left: 50%; top: 40%; }
            40%  { left: 18%; top: 54%; }
            48%  { left: 56%; top: 60%; }
            56%  { left: 34%; top: 72%; }
            64%  { left: 22%; top: 44%; }
            72%  { left: 54%; top: 34%; }
            80%  { left: 40%; top: 56%; }
            90%  { left: 16%; top: 66%; }
            100% { left: 10%; top: 14%; }
          }
          @keyframes poc-lens-breathe {
            0% { opacity: .55; transform: scale(.96); }
            50% { opacity: .9; transform: scale(1); }
            100% { opacity: .55; transform: scale(.96); }
          }
          @keyframes poc-lens-wobble {
            0% { transform: rotate(-2deg); }
            50% { transform: rotate(2deg); }
            100% { transform: rotate(-2deg); }
          }
          @media (prefers-reduced-motion: reduce) {
            .poc-float,
            .poc-lens,
            .poc-scanBeam {
              animation: none !important;
            }
          }
        `}
      </style>

      <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/20 p-6">
        {/* ambient glow */}
        <div className="pointer-events-none absolute -left-20 -bottom-24 size-64 rounded-full bg-blue-500/5 dark:bg-blue-400/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-indigo-500/5 dark:bg-indigo-400/10 blur-3xl" />

        {/* paper (calendar) */}
        <div
          className="poc-float relative mx-auto mt-2 w-full max-w-[360px] overflow-hidden rounded-xl border border-border bg-card shadow-[0_28px_70px_-50px_rgba(0,0,0,0.65)]"
          style={{ animation: "poc-float 3.4s ease-in-out infinite" }}
        >
          <div className="px-6 pt-6 pb-7">
            <div className="text-sm font-semibold text-foreground/80">
              Calendar
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{rangeLabel}</div>

            <div className="mt-5 grid grid-cols-7 gap-2">
              {dayLabels.map((day, index) => (
                <div
                  key={index}
                  className="text-[10px] font-medium text-muted-foreground text-center"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="relative mt-2">
              <div className="grid grid-cols-7 gap-2">
                {dayCells.map((n) => (
                  <div
                    key={n}
                    className="h-7 rounded-md border border-border/60 bg-muted/25"
                  />
                ))}
              </div>

              {/* magnifier (within day cells) */}
              <div
                className="poc-lens pointer-events-none absolute left-0 top-0"
                style={{ animation: "poc-lens-travel 20s ease-in-out infinite" }}
              >
                <div className="relative flex items-center gap-2">
                  {/* lens glow (rebuilt): soft professional glow + subtle highlight (no harsh rings) */}
                  <div className="pointer-events-none absolute -inset-8">
                    {/* broad soft glow */}
                    <div
                      className="absolute inset-0 rounded-[999px]"
                      style={{
                        background:
                          "radial-gradient(ellipse 135% 115% at 42% 42%, hsl(var(--primary) / 0.30) 0%, hsl(var(--primary) / 0.14) 42%, transparent 82%)",
                        filter: "blur(22px)",
                        animation: "poc-lens-breathe 2.2s ease-in-out infinite"
                      }}
                    />
                    {/* tighter inner bloom for depth */}
                    <div
                      className="absolute inset-6 rounded-[999px]"
                      style={{
                        background:
                          "radial-gradient(ellipse 130% 110% at 46% 50%, hsl(var(--primary) / 0.26) 0%, transparent 68%)",
                        filter: "blur(12px)",
                        opacity: 1
                      }}
                    />
                    {/* faint specular highlight (ties into card lighting) */}
                    <div
                      className="absolute inset-7 rounded-[999px]"
                      style={{
                        background:
                          "radial-gradient(ellipse 120% 95% at 28% 30%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.07) 24%, transparent 48%)",
                        opacity: 0.85
                      }}
                    />
                  </div>

                  <div
                    className="relative flex size-10 items-center justify-center rounded-full border border-border bg-background/80"
                    style={{
                      boxShadow:
                        "0 0 0 1px hsl(var(--primary) / 0.10), 0 18px 36px -26px hsl(var(--primary) / 0.35)"
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 30%, hsl(var(--foreground) / 0.10), transparent 58%)",
                        opacity: 0.6
                      }}
                    />
                    <SearchIcon className="relative size-5 text-foreground/70" />
                  </div>

                  {/* range label: travels with the magnifier */}
                  <div
                    className="relative inline-flex items-center rounded-full border border-border bg-background/80 px-2.5 py-1 text-[10px] text-foreground/80"
                    style={{
                      boxShadow:
                        "0 0 0 1px hsl(var(--foreground) / 0.04), 0 12px 22px -18px rgba(0,0,0,0.55)"
                    }}
                  >
                    <span className="truncate max-w-[120px] font-medium">{rangeLabel}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
